// ==================== SpecialistCard.tsx ====================
import {useState, useEffect, useRef} from 'react';
import {Button, Icon, Text} from '@/ui-kits';
import {appendPlural, cn, formatName} from '@/utils/common_functions';
import {Doctor} from '@/interface';
import {HospitalTypeLabel, Images} from '@/constants';
import {useSelector} from 'react-redux';
import {accountDeletionStatusSelector} from '@/services/redux/selectors';
import {PatientAccountTicketRequestStatus, SpecialistEntityType} from '@/constants';
import {Routes} from '@/navigation/Routes';
import {useNavigate} from 'react-router-dom';

interface SpecialistCardProps {
  className?: string;
  disabled?: boolean;
  doctor: Doctor;
  readonly?: boolean;
  isSelected?: boolean;
  onSelect?: (id: number | null) => void;
  onBookNow?: (doctor: Doctor) => void;
  onHospitalBooking?: (doctor: Doctor) => void;
  isPopular?: boolean;
  showBookNow?: boolean;
  compact?: boolean;
  nameSize?: string;
  isFavorite?: boolean;
  onFavoriteToggle?: (doctorId: number) => void;
  showFavoriteIcon?: boolean;
  lastConsultedDate?: string | null;
}

export const SpecialistCard: React.FC<SpecialistCardProps> = ({
  className,
  disabled = false,
  doctor,
  readonly = false,
  isSelected = false,
  onSelect,
  onBookNow,
  onHospitalBooking,
  showBookNow = false,
  compact = false,
  nameSize,
  isFavorite = false,
  onFavoriteToggle,
  showFavoriteIcon = false,
  lastConsultedDate,
}) => {
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const navigate = useNavigate();
  const [showHospitalTooltip, setShowHospitalTooltip] = useState(false);
  const isLongHospitalName = (doctor.hospital_name?.length ?? 0) > 25;
  const hospitalTooltipRef = useRef<HTMLDivElement>(null);

  // Format date to DD-MM-YYYY
  const formatLastConsultedDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'});
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hospitalTooltipRef.current && !hospitalTooltipRef.current.contains(event.target as Node)) {
        setShowHospitalTooltip(false);
      }
    };

    if (showHospitalTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showHospitalTooltip]);

  const handleViewProfile = () => {
    if (doctor.entity_type === SpecialistEntityType.Specialist) {
      navigate(Routes.SPECIALIST_PROFILE_VIEW.replace(':id', doctor.id.toString()), {
        state: {doctor},
      });
    } else {
      navigate(Routes.HOSPITAL_PROFILE_VIEW.replace(':id', doctor.id.toString()), {
        state: {doctor},
      });
    }
  };
  return (
    <div className={cn('relative w-full flex-shrink-0 h-full max-w-[280px]', className, doctor?.entity_type === SpecialistEntityType.Specialist ? '' : '')}>
      <div className={cn('relative bg-white border border-ui_input_border/40 rounded-lg shadow-sm hover:shadow-md transition-all h-full flex flex-col')}>
        {/* Blue Background Section with Image */}
        <div
          className={cn(
            'relative bg-[#CBEBFB] flex items-end justify-center rounded-lg rounded-b-none overflow-visible flex-shrink-0',
            compact ? 'h-[130px]' : 'h-[140px] sm:h-[155px]',
          )}
          style={{zIndex: 1}}>
          {doctor?.popular && (
            <div
              className={cn(
                'absolute top-2.5 left-2.5 bg-[#FFD633] text-ui_blue font-PoppinsSemiBold rounded-full z-10',
                compact ? 'text-[10px] px-2.5 py-0.5' : 'text-xs px-3 py-1',
              )}>
              Popular
            </div>
          )}

          {/* Last Consulted Tag */}
          {lastConsultedDate && (
            <div
              className={cn(
                'absolute flex flex-col left-2.5 bg-white text-ui_blue rounded-xl z-10 shadow-sm border border-ui_input_border/20',
                doctor?.popular ? (compact ? 'top-10' : 'top-11') : 'top-2.5',
                compact ? 'text-[9px] px-2 py-1 w-auto max-w-[85px]' : 'text-[8px] px-2.5 py-1 w-[75px]',
              )}>
              <span className="font-PoppinsRegular block text-nowrap text-[8px]">Last consulted</span>
              <span className="font-PoppinsSemiBold block text-nowrap">{formatLastConsultedDate(lastConsultedDate)}</span>
            </div>
          )}

          {/* Favourite Icon */}
          {showFavoriteIcon && doctor.entity_type === SpecialistEntityType.Specialist && (
            <button
              onClick={e => {
                e.stopPropagation();
                onFavoriteToggle?.(doctor.id);
              }}
              className={cn('absolute top-2.5 right-2.5 z-10 p-1 transition-colors', compact ? 'size-6' : 'size-7')}>
              <Icon type={doctor?.is_favourite ? 'heart_red' : 'heart_empty'} className="flex-shrink-0 size-7" />
            </button>
          )}

          {/* Doctor Image */}
          {doctor.image && (
            <img
              src={doctor.image}
              alt={doctor.name}
              className={cn(
                doctor.entity_type === SpecialistEntityType.Specialist
                  ? 'w-auto object-contain object-bottom'
                  : 'w-full object-contain h-full object-center rounded-t-2xl',
                doctor.entity_type === SpecialistEntityType.Specialist ? (compact ? 'h-[145px]' : 'h-[165px] sm:h-[175px] md:h-[185px]') : 'h-full',
              )}
            />
          )}
        </div>

        {/* Content Section */}
        <div className={cn('flex-1 flex flex-col', compact ? 'px-3 pt-2 pb-2.5' : 'px-3.5 pt-2.5 pb-3')}>
          {/* Name and Designation/Address */}
          <div className="mb-3">
            <h2 className={cn('font-FigtreeBold text-ui_blue mb-0 leading-tight', nameSize || (compact ? 'text-md' : 'text-[15px]'))}>
              {doctor.entity_type === SpecialistEntityType.Specialist ? 'Dr. ' : ''}
              {formatName(doctor.name)}
            </h2>
            {doctor.entity_type === SpecialistEntityType.Specialist && doctor.specialization?.label && (
              <p className={cn('text-ui_input_border_dark font-FigtreeMedium leading-tight mt-0.5', compact ? 'text-[12px]' : 'text-xs')}>
                {doctor.specialization.label}
              </p>
            )}
            {doctor.entity_type === SpecialistEntityType.Specialist && (
              <p
                className={cn(
                  'bg-gradient-to-r from-ui_blue to-ui_blue_light bg-clip-text text-transparent font-FigtreeSemiBold leading-tight',
                  compact ? 'text-[13px]' : 'text-sm',
                )}>
                {formatName(doctor.designation)}
              </p>
            )}
          </div>

          {/* Education */}
          {doctor.entity_type === SpecialistEntityType.Specialist && (
            <div className="flex items-start gap-1.5 mb-1.5">
              <Icon type="education_cap" className="mt-0.5 flex-shrink-0 size-3.5" />
              <span className={cn('font-FigtreeRegular leading-snug text-ui_blue', compact ? 'text-[12px]' : 'text-sm')}>{formatName(doctor.education)}</span>
            </div>
          )}

          {/* hospital phone and type */}
          {doctor.entity_type === SpecialistEntityType.Hospital && (
            <div className={cn('flex gap-1.5 mb-2 items-center justify-between')}>
              <Text className="text-ui_blue !text-[13px]">{HospitalTypeLabel[doctor?.hospital_type?.id as keyof typeof HospitalTypeLabel]}</Text>
              <div className={cn('flex gap-1.5 items-center')}>
                <Icon type="phone" className={cn('flex-shrink-0 size-3 ')} />
                <span className={cn('font-FigtreeRegular text-ui_blue text-[12px]')}>{doctor?.phone}</span>
              </div>
            </div>
          )}

          {/* hospital email */}
          {doctor.entity_type === SpecialistEntityType.Hospital && (
            <div className={cn('flex gap-1.5 mb-3 mt-2 items-start min-w-0 overflow-hidden')}>
              <Icon type="email" className={cn('flex-shrink-0 size-3 mt-0.5')} />
              <span className={cn('font-FigtreeRegular text-ui_blue break-words min-w-0 flex-1', compact ? 'text-[13px]' : 'text-sm')}>{doctor?.email}</span>
            </div>
          )}

          {/* Location or Hospital Info */}
          {doctor.is_hospital_specialist && (doctor.hospital_name || doctor.hospital_address) ? (
            <div className="relative mb-2" ref={hospitalTooltipRef}>
              {showHospitalTooltip && isLongHospitalName && (
                <div className={cn('absolute bottom-full left-0 mb-2 bg-white border-2 border-[#4DBD9F] rounded-xl px-1 py-1 z-20 w-max max-w-[260px]')}>
                  <Text variant="body" className="!font-FigtreeBold !text-sm text-[#0C8363] whitespace-normal leading-tight">
                    {doctor.hospital_name}
                  </Text>
                </div>
              )}
              <div className={cn('flex gap-1.5 bg-[#EFFEED] border-[#4DBD9F] border border-l-4 p-1 rounded-md items-start min-w-0 overflow-hidden')}>
                <Icon type="hospital_specialist" className="flex-shrink-0 w-[30px] h-[26px] text-[#0C8363]" />
                <div className="break-words min-w-0 flex-1 text-xs text-[#0C8363]">
                  <div className="flex items-center gap-1">
                    <Text variant="body" className="!font-FigtreeBold line-clamp-1">
                      {doctor.hospital_name ?? ''}
                    </Text>
                    {isLongHospitalName && (
                      <button
                        onClick={() => setShowHospitalTooltip(prev => !prev)}
                        className="flex-shrink-0 p-0.5 hover:opacity-70 transition-opacity"
                        title="Show full hospital name">
                        <Icon type="i_icon" className="flex-shrink-0 size-3.5 text-[#0C8363]" />
                      </button>
                    )}
                  </div>
                  <span className='flex items-center gap-1 mt-0.5'>
                    <Icon type="location" className={cn('flex-shrink-0 size-3')} />
                    <Text variant="tiny" className="text-ui_blue">
                      {doctor.hospital_address ?? ''}
                    </Text>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className={cn('flex gap-1.5 mb-2 items-start min-w-0 overflow-hidden')}>
              <Icon type="location" className={cn('flex-shrink-0 size-3 mt-0.5')} />
              <span className={cn('font-FigtreeRegular text-ui_blue break-words min-w-0 flex-1', compact ? 'text-[13px]' : 'text-sm')}>
                {doctor.entity_type === SpecialistEntityType.Specialist ? doctor.location?.label || 'India' : doctor.address || 'India'}
              </span>
            </div>
          )}

          {/* Experience and Rating */}
          {doctor.entity_type === SpecialistEntityType.Specialist && (
            <div className={cn('flex justify-between items-center border-t border-gray-200 mb-2 mt-auto pt-3')}>
              <span className={cn('text-ui_blue font-FigtreeRegular', compact ? 'text-[12px]' : 'text-sm')}>
                {doctor.experience} {appendPlural('Year', doctor.experience)}
              </span>
              <span className={cn('flex items-center gap-1 text-ui_blue font-FigtreeBold', compact ? 'text-[12px]' : 'text-sm')}>
                {doctor.rating ? (
                  <>
                    {doctor.rating}/5 <Icon type="star_yellow" className="text-[#FAE100] fill-[#FAE100] size-3" />
                  </>
                ) : null}
              </span>
            </div>
          )}

          {/* divder */}
          {doctor.entity_type === SpecialistEntityType.Hospital && <div className={cn('h-[1px] bg-gray-200 my-2')} />}

          {/* Consultation Fee */}
          {!readonly && (
            <div className="flex justify-between items-baseline mb-2.5">
              <span className={cn('text-ui_blue font-FigtreeRegular', compact ? 'text-[12px]' : 'text-sm')}>Consultation Fee</span>
              <span className={cn('font-FigtreeBold text-ui_blue tabular-nums', compact ? 'text-xl' : 'text-xl')}>₹ {doctor.price}</span>
            </div>
          )}

          {/* Action Buttons */}
          {!readonly && (
            <div className="flex items-center gap-2">
              {/* View Profile Button */}
              {/* <div className="flex-1 rounded-lg bg-gradient-to-r p-[1px] flex items-center from-ui_blue to-[#447DA8]">
                <button
                  disabled={disabled}
                  onClick={handleViewProfile}
                  className={cn(
                    'flex-1 w-full bg-white font-PoppinsSemiBold rounded-[7px] hover:bg-gray-50 transition-colors duration-200 whitespace-nowrap',
                    compact ? 'text-xs px-2 py-[6px]' : 'text-sm px-3 py-2',
                  )}>
                  <span className="bg-gradient-to-r from-ui_blue to-[#447DA8] bg-clip-text text-transparent">View Profile</span>
                </button>
              </div> */}
              <Button
                title="View Profile"
                onClick={handleViewProfile}
                disabled={disabled}
                variant="secondary"
                className={cn(
                  'flex-1 w-full bg-white font-PoppinsSemiBold rounded-[7px] hover:bg-gray-50 transition-colors duration-200 whitespace-nowrap',
                  compact ? 'px-2 py-1.5' : 'px-3 py-2',
                  'rounded-lg w-full',
                )}
                textClassName={compact ? '!text-[10px]' : 'text-xs'}
              />

              {/* Select/Book Now Button */}
              {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
                <button
                  disabled={disabled}
                  onClick={() => {
                    if (showBookNow) {
                      // For hospitals, use onHospitalBooking if available, otherwise fallback to onBookNow
                      if (doctor.entity_type === SpecialistEntityType.Hospital && onHospitalBooking) {
                        onHospitalBooking(doctor);
                      } else if (onBookNow) {
                        onBookNow(doctor);
                      }
                    } else {
                      onSelect?.(isSelected ? null : doctor.id);
                    }
                  }}
                  className={cn(
                    'flex-1 rounded-lg text-white font-PoppinsSemiBold transition-all duration-200 whitespace-nowrap',
                    compact ? 'text-xs px-2 py-1.5' : 'text-sm px-3 py-2',
                    isSelected ? 'bg-green-600 hover:bg-green-700' : 'bg-gradient-to-r from-ui_blue to-ui_blue_light hover:from-ui_blue hover:to-ui_blue_light',
                    disabled && 'opacity-50 cursor-not-allowed',
                  )}>
                  {showBookNow ? 'Book Now' : isSelected ? 'Selected' : 'Select'}
                </button>
              )}

              {/* <Button 
                title={showBookNow ? 'Book Now' : isSelected ? 'Selected' : 'Select'}
                onClick={() => {
                  if (showBookNow && onBookNow) {
                    onBookNow(doctor);
                  } else {
                    onSelect?.(isSelected ? null : doctor.id);
                  }
                }}
                disabled={disabled}
                variant='primary'
                className={cn(compact ? 'px-2 py-1.5' : 'px-3 py-2', 'rounded-lg w-full', isSelected
                    &&'!bg-gradient-to- !from:green-600 !to:green-600 !hover:bg-green-700')}
                textClassName={compact ? '!text-[10px]' : 'text-xs'}
              /> */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
